/**
 * @fileoverview Notification preferences form state.
 * @description Holds the user's unsaved toggles ("drafts") over the loaded preferences, saves only
 * the changed rows through PUT /notifications/preferences, and clears the drafts once the refetched
 * rows are in. A rejected save keeps the drafts, so the user can fix or retry; a stale-version
 * rejection (409/6007) refetches first, so the retry carries the latest rowVersions. A toggle set
 * back to its saved value is dropped from the drafts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

import { useUpdateNotificationPreferences } from '@/features/notifications/hooks/useUpdateNotificationPreferences'
import {
  buildPreferenceUpdates,
  preferenceTargetId,
  savedValue,
} from '@/features/notifications/utils/notification-preferences.utils'

import type { NotificationPreference } from '@/features/notifications/services/notification-preferences.service'
import type { PreferenceDrafts } from '@/features/notifications/utils/notification-preferences.utils'

/**
 * @description Draft state and save/discard actions for the preferences panel.
 * @param {NotificationPreference[]} preferences - The latest rows from the server.
 * @returns {object} drafts, changeCount, isSaving, isError, error, justSaved, toggle, save, discard.
 */
export function useNotificationPreferencesForm(preferences: NotificationPreference[]) {
  const [drafts, setDrafts] = useState<PreferenceDrafts>({})
  const [justSaved, setJustSaved] = useState(false)
  const mutation = useUpdateNotificationPreferences()
  const updates = buildPreferenceUpdates(preferences, drafts)

  /**
   * @description Sets one cell's draft, or drops it when it equals the saved value.
   * @param {NotificationPreference} preference - The toggled cell.
   * @param {boolean} next - The new checked state.
   */
  const toggle = (preference: NotificationPreference, next: boolean) => {
    const id = preferenceTargetId(preference)
    setJustSaved(false)
    mutation.reset()
    setDrafts((current) => {
      const rest = Object.fromEntries(Object.entries(current).filter(([key]) => key !== id))
      return next === savedValue(preference) ? rest : { ...rest, [id]: next }
    })
  }

  /**
   * @description Saves the changed rows; the drafts clear once the refetched rows are in.
   */
  const save = () => {
    if (updates.length === 0) return
    mutation.mutate(updates, {
      /**
       * @description Clears the drafts and shows the saved note.
       */
      onSuccess: () => {
        setDrafts({})
        setJustSaved(true)
      },
    })
  }

  /**
   * @description Throws away every unsaved toggle.
   */
  const discard = () => {
    mutation.reset()
    setDrafts({})
  }

  return {
    drafts,
    changeCount: updates.length,
    isSaving: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    justSaved,
    toggle,
    save,
    discard,
  }
}
